'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Briefcase,
  Search,
  Building2,
  MapPin,
  Clock,
  DollarSign,
  PlusCircle,
  CheckCircle2,
  Send,
  Lock,
  LogIn,
  Upload,
  X,
  FileText,
  Image as ImageIcon,
  MessageCircle,
  Reply,
  Trash2,
  Eye,
  Loader2,
  ChevronDown,
  Globe,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { api, ApiError } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────

interface JobMedia {
  uuid: string;
  original_name: string;
  file_path: string;
  mime_type: string;
  file_size: number;
  type: string;
}

interface JobPoster {
  uuid: string;
  name: string;
  avatar: string | null;
  alumni_profile?: {
    cohort_name: string;
    entry_year: number;
    graduation_year: number | null;
  } | null;
}

interface JobComment {
  uuid: string;
  body: string;
  user: { uuid: string; name: string; avatar: string | null };
  created_at: string;
  replies?: JobComment[];
}

interface JobPost {
  uuid: string;
  title: string;
  slug: string;
  type: string;
  description: string;
  qualifications: string | null;
  location: string | null;
  is_remote: boolean;
  salary_min: string | null;
  salary_max: string | null;
  salary_currency: string;
  salary_visible: boolean;
  contact_info: string | null;
  application_url: string | null;
  status: string;
  published_at: string;
  expires_at: string | null;
  view_count: number;
  posted_by: JobPoster;
  company: { name: string; logo: string | null; slug: string } | null;
  industry: { name: string } | null;
  media: JobMedia[];
  comments_count?: number;
  root_comments?: JobComment[];
}

const JOB_TYPES: Record<string, string> = {
  fulltime: 'Full-time',
  parttime: 'Part-time',
  internship: 'Magang',
  freelance: 'Freelance',
  contract: 'Kontrak',
  tender: 'Tender',
  business_opportunity: 'Peluang Bisnis',
  partnership: 'Kemitraan',
};

function getApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const envUrl = process.env.NEXT_PUBLIC_API_URL;
    if (envUrl) return envUrl.replace(/\/api$/, '');
    return window.location.origin;
  }
  return '';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatSalary(min: string | null, max: string | null, currency: string): string {
  if (!min && !max) return 'Tidak disebutkan';
  const fmt = (val: string) => {
    const num = parseFloat(val);
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + ' Jt';
    if (num >= 1_000) return (num / 1_000).toFixed(0) + ' Rb';
    return num.toLocaleString('id-ID');
  };
  if (min && max) return `${currency} ${fmt(min)} – ${fmt(max)}`;
  if (min) return `${currency} ${fmt(min)}+`;
  return `s/d ${currency} ${fmt(max!)}`;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return 'Baru saja';
  if (diff < 3600) return Math.floor(diff / 60) + ' menit lalu';
  if (diff < 86400) return Math.floor(diff / 3600) + ' jam lalu';
  if (diff < 604800) return Math.floor(diff / 86400) + ' hari lalu';
  return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Job Post Form Dialog ──────────────────────────────────

function CreateJobDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [form, setForm] = useState({
    title: '',
    type: 'fulltime',
    description: '',
    qualifications: '',
    location: '',
    is_remote: false,
    salary_min: '',
    salary_max: '',
    salary_visible: true,
    contact_info: '',
    application_url: '',
    expires_at: '',
  });

  const updateField = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const total = files.length + newFiles.length;
    if (total > 5) {
      toast.error('Maksimal 5 file per lowongan');
      return;
    }
    // Validate size (10MB each)
    for (const f of newFiles) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`File "${f.name}" melebihi batas 10MB`);
        return;
      }
    }
    setFiles((prev) => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.description || form.description.length < 20) {
      toast.error('Judul dan deskripsi (min 20 karakter) wajib diisi');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('type', form.type);
      formData.append('description', form.description);
      if (form.qualifications) formData.append('qualifications', form.qualifications);
      if (form.location) formData.append('location', form.location);
      formData.append('is_remote', form.is_remote ? '1' : '0');
      if (form.salary_min) formData.append('salary_min', form.salary_min);
      if (form.salary_max) formData.append('salary_max', form.salary_max);
      formData.append('salary_visible', form.salary_visible ? '1' : '0');
      if (form.contact_info) formData.append('contact_info', form.contact_info.trim());
      if (form.application_url) {
        let appUrl = form.application_url.trim();
        if (appUrl) {
          if (!/^https?:\/\//i.test(appUrl)) {
            appUrl = `https://${appUrl}`;
          }
          formData.append('application_url', appUrl);
        }
      }
      if (form.expires_at) formData.append('expires_at', form.expires_at);

      files.forEach((file) => {
        formData.append('media[]', file);
      });

      await api.upload('/job-posts', formData);
      toast.success('Lowongan berhasil dipublikasikan! 🎉');
      onOpenChange(false);
      setForm({
        title: '',
        type: 'fulltime',
        description: '',
        qualifications: '',
        location: '',
        is_remote: false,
        salary_min: '',
        salary_max: '',
        salary_visible: true,
        contact_info: '',
        application_url: '',
        expires_at: '',
      });
      setFiles([]);
      onCreated();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.errors && Object.keys(apiErr.errors).length > 0) {
        const firstField = Object.keys(apiErr.errors)[0];
        const firstMsg = Array.isArray(apiErr.errors[firstField]) ? apiErr.errors[firstField][0] : apiErr.errors[firstField];
        toast.error(firstMsg || apiErr.message || 'Gagal mempublikasikan lowongan');
      } else {
        toast.error(apiErr.message || 'Gagal mempublikasikan lowongan');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-0 border-[#E2E8F0]">
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-[#E2E8F0] px-6 py-4 rounded-t-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading font-black text-[#1F2937]">
              Pasang Lowongan Baru
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Bagikan peluang karir dari perusahaan Anda kepada sesama alumni.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-5">
          {/* Title */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#1F2937]">
              Judul Posisi <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="Contoh: Senior Mechanical Engineer"
              className="rounded-xl h-10 text-sm border-[#E2E8F0] focus-visible:ring-[#004A99]"
              required
            />
          </div>

          {/* Type */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#1F2937]">
              Tipe Pekerjaan <span className="text-red-500">*</span>
            </label>
            <select
              value={form.type}
              onChange={(e) => updateField('type', e.target.value)}
              className="w-full h-10 px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-[#1F2937] text-sm focus:outline-none focus:ring-2 focus:ring-[#004A99]"
            >
              {Object.entries(JOB_TYPES).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#1F2937]">
              Deskripsi Pekerjaan <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Jelaskan tanggung jawab, lingkup kerja, dan informasi penting lainnya..."
              rows={4}
              className="w-full px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-[#1F2937] text-sm focus:outline-none focus:ring-2 focus:ring-[#004A99] resize-none"
              required
              minLength={20}
            />
          </div>

          {/* Qualifications */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#1F2937]">Kualifikasi & Persyaratan</label>
            <textarea
              value={form.qualifications}
              onChange={(e) => updateField('qualifications', e.target.value)}
              placeholder="- Minimal S1 Teknik Mesin&#10;- Pengalaman 3 tahun..."
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-[#1F2937] text-sm focus:outline-none focus:ring-2 focus:ring-[#004A99] resize-none"
            />
          </div>

          {/* Location + Remote */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#1F2937]">Lokasi</label>
              <Input
                value={form.location}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder="Jakarta, Surabaya, dll"
                className="rounded-xl h-10 text-sm border-[#E2E8F0] focus-visible:ring-[#004A99]"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_remote}
                  onChange={(e) => updateField('is_remote', e.target.checked)}
                  className="w-4 h-4 rounded border-[#E2E8F0] text-[#004A99] focus:ring-[#004A99]"
                />
                <span className="text-xs font-medium text-[#1F2937] flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5 text-[#004A99]" />
                  Remote / WFH
                </span>
              </label>
            </div>
          </div>

          {/* Salary */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#1F2937]">Rentang Gaji (IDR)</label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                value={form.salary_min}
                onChange={(e) => updateField('salary_min', e.target.value)}
                placeholder="Minimum"
                className="rounded-xl h-10 text-sm border-[#E2E8F0] focus-visible:ring-[#004A99]"
              />
              <Input
                type="number"
                value={form.salary_max}
                onChange={(e) => updateField('salary_max', e.target.value)}
                placeholder="Maksimum"
                className="rounded-xl h-10 text-sm border-[#E2E8F0] focus-visible:ring-[#004A99]"
              />
            </div>
            <label className="flex items-center gap-2 mt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={form.salary_visible}
                onChange={(e) => updateField('salary_visible', e.target.checked)}
                className="w-4 h-4 rounded border-[#E2E8F0] text-[#004A99] focus:ring-[#004A99]"
              />
              <span className="text-xs text-[#64748B]">Tampilkan gaji ke pelamar</span>
            </label>
          </div>

          {/* Contact & URL */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#1F2937]">Info Kontak / Email</label>
              <Input
                value={form.contact_info}
                onChange={(e) => updateField('contact_info', e.target.value)}
                placeholder="hr@company.com"
                className="rounded-xl h-10 text-sm border-[#E2E8F0] focus-visible:ring-[#004A99]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#1F2937]">Link Lamaran</label>
              <Input
                value={form.application_url}
                onChange={(e) => updateField('application_url', e.target.value)}
                placeholder="https://... atau namadomain.com"
                className="rounded-xl h-10 text-sm border-[#E2E8F0] focus-visible:ring-[#004A99]"
              />
            </div>
          </div>

          {/* Expiry */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#1F2937]">Batas Waktu Lowongan</label>
            <Input
              type="date"
              value={form.expires_at}
              onChange={(e) => updateField('expires_at', e.target.value)}
              className="rounded-xl h-10 text-sm border-[#E2E8F0] focus-visible:ring-[#004A99] w-fit"
            />
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-[#1F2937]">
              Lampiran ({files.length}/5)
            </label>
            <div className="border-2 border-dashed border-[#E2E8F0] rounded-xl p-4 text-center hover:border-[#004A99]/40 transition-colors">
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
                className="hidden"
                id="job-file-upload"
                disabled={files.length >= 5}
              />
              <label
                htmlFor="job-file-upload"
                className="cursor-pointer flex flex-col items-center gap-1"
              >
                <Upload className="h-6 w-6 text-[#64748B]" />
                <span className="text-xs text-[#64748B]">
                  Klik untuk upload gambar, PDF, atau dokumen (maks 10MB/file)
                </span>
              </label>
            </div>

            {files.length > 0 && (
              <div className="space-y-1.5">
                {files.map((file, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2 text-xs"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {file.type.startsWith('image/') ? (
                        <ImageIcon className="h-4 w-4 text-blue-500 shrink-0" />
                      ) : (
                        <FileText className="h-4 w-4 text-red-500 shrink-0" />
                      )}
                      <span className="truncate font-medium text-[#1F2937]">{file.name}</span>
                      <span className="text-[#64748B] shrink-0">{formatFileSize(file.size)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-red-400 hover:text-red-600 shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-2 border-t border-[#E2E8F0]">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-xl border-[#E2E8F0] text-xs font-semibold"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-xl bg-[#D71920] hover:bg-[#B8141B] text-white font-bold text-xs shadow-sm"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Mempublikasikan...
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  Publikasikan Lowongan
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Comment Section ────────────────────────────────────────

function CommentSection({ jobUuid }: { jobUuid: string }) {
  const { isAuthenticated, user } = useAuth();
  const [comments, setComments] = useState<JobComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState<{ uuid: string; name: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const res = await api.get<{ data: JobComment[] }>(`/job-posts/${jobUuid}/comments`);
      setComments(res.data || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [jobUuid]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      const payload: Record<string, string> = { body: body.trim() };
      if (replyTo) payload.parent_id = replyTo.uuid;

      await api.post(`/job-posts/${jobUuid}/comments`, payload);
      setBody('');
      setReplyTo(null);
      fetchComments();
      toast.success('Komentar berhasil ditambahkan');
    } catch (err) {
      const apiErr = err as ApiError;
      toast.error(apiErr.message || 'Gagal menambahkan komentar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentUuid: string) => {
    if (!confirm('Hapus komentar ini?')) return;
    try {
      await api.delete(`/job-posts/${jobUuid}/comments/${commentUuid}`);
      fetchComments();
      toast.success('Komentar dihapus');
    } catch {
      toast.error('Gagal menghapus komentar');
    }
  };

  const renderComment = (comment: JobComment, isReply = false) => (
    <div key={comment.uuid} className={`${isReply ? 'ml-8 border-l-2 border-blue-100 pl-4' : ''}`}>
      <div className="flex gap-2.5 py-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#004A99] to-[#0066CC] flex items-center justify-center text-white text-xs font-bold shrink-0">
          {comment.user.avatar ? (
            <img src={comment.user.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            comment.user.name.charAt(0).toUpperCase()
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-xs text-[#1F2937]">{comment.user.name}</span>
            <span className="text-[10px] text-[#94A3B8]">{timeAgo(comment.created_at)}</span>
          </div>
          <p className="text-xs text-[#475569] mt-1 leading-relaxed whitespace-pre-wrap">{comment.body}</p>
          <div className="flex items-center gap-3 mt-1.5">
            {isAuthenticated && !isReply && (
              <button
                onClick={() => setReplyTo({ uuid: comment.uuid, name: comment.user.name })}
                className="text-[10px] text-[#64748B] hover:text-[#004A99] font-medium flex items-center gap-1 transition-colors"
              >
                <Reply className="h-3 w-3" />
                Balas
              </button>
            )}
            {user && comment.user.uuid === user.uuid && (
              <button
                onClick={() => handleDelete(comment.uuid)}
                className="text-[10px] text-[#64748B] hover:text-red-500 font-medium flex items-center gap-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Hapus
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Render replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-0">
          {comment.replies.map((reply) => renderComment(reply, true))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <h4 className="font-heading font-bold text-sm text-[#1F2937] flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-[#004A99]" />
        Diskusi ({comments.length})
      </h4>

      {/* Comment Input */}
      {isAuthenticated ? (
        <div className="space-y-2">
          {replyTo && (
            <div className="flex items-center gap-2 text-[10px] text-[#004A99] bg-blue-50 px-3 py-1.5 rounded-lg">
              <Reply className="h-3 w-3" />
              <span>Membalas <strong>{replyTo.name}</strong></span>
              <button onClick={() => setReplyTo(null)} className="ml-auto text-slate-400 hover:text-slate-600">
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={replyTo ? `Balas komentar ${replyTo.name}...` : 'Tulis komentar...'}
              rows={2}
              className="flex-1 px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-[#1F2937] text-xs focus:outline-none focus:ring-2 focus:ring-[#004A99] resize-none"
              maxLength={2000}
            />
            <Button
              type="button"
              size="sm"
              disabled={!body.trim() || submitting}
              onClick={handleSubmit}
              className="rounded-xl bg-[#004A99] hover:bg-[#003A7A] text-white self-end h-9"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center py-3 text-xs text-[#64748B] bg-slate-50 rounded-xl">
          <Link href="/login" className="text-[#004A99] font-semibold hover:underline">Masuk</Link> untuk bergabung dalam diskusi
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 text-[#64748B] animate-spin" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-center text-xs text-[#94A3B8] py-4">Belum ada komentar. Jadilah yang pertama!</p>
      ) : (
        <div className="divide-y divide-[#F1F5F9]">
          {comments.map((c) => renderComment(c))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────

export default function KarirPage() {
  const { isAuthenticated, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedJob, setSelectedJob] = useState<JobPost | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [jobs, setJobs] = useState<JobPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const isAlumni = user?.user_type === 'alumni';

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (selectedType !== 'all') params.set('type', selectedType);

      const res = await api.get<{ data: JobPost[] }>(`/job-posts?${params.toString()}`);
      setJobs(res.data || []);
    } catch {
      toast.error('Gagal memuat lowongan');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedType]);

  useEffect(() => {
    const timer = setTimeout(fetchJobs, 300);
    return () => clearTimeout(timer);
  }, [fetchJobs]);

  const openJobDetail = async (job: JobPost) => {
    setLoadingDetail(true);
    setSelectedJob(job); // show immediately with partial data
    try {
      const res = await api.get<{ data: JobPost }>(`/job-posts/${job.uuid}`);
      setSelectedJob(res.data);
    } catch {
      // keep partial data
    } finally {
      setLoadingDetail(false);
    }
  };

  const handlePostClick = () => {
    if (!isAuthenticated) {
      toast.info('Silakan login sebagai alumni untuk memposting lowongan');
      return;
    }
    if (!isAlumni) {
      toast.info('Hanya alumni yang dapat memposting lowongan');
      return;
    }
    setShowCreateDialog(true);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background selection:bg-[#D71920] selection:text-white">
      <Navbar />

      <main className="flex-1 pb-20">
        {/* Banner Section */}
        <section className="relative border-b-4 border-[#D71920] bg-[linear-gradient(135deg,#021024_0%,#062347_35%,#004A99_75%,#0066CC_100%)] py-12 lg:py-14 text-white overflow-hidden shadow-lg">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:32px_32px] opacity-65 pointer-events-none" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-[radial-gradient(circle_at_80%_20%,rgba(215,25,32,0.28),transparent_70%)] pointer-events-none" />
          <div className="absolute -bottom-10 left-1/4 w-80 h-80 bg-[radial-gradient(circle,rgba(0,102,204,0.35),transparent_70%)] pointer-events-none" />
          <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="max-w-2xl space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-200">
                  <Briefcase className="h-3.5 w-3.5 text-red-400" />
                  <span>BURSA KERJA & REKRUTMEN EKSKLUSIF ALUMNI</span>
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                  Peluang Karir Teknik Mesin
                </h1>
                <p className="text-sm sm:text-base text-slate-200 leading-relaxed">
                  Lowongan kerja terverifikasi yang direkomendasikan langsung oleh alumni Teknik Mesin ITN Malang dan perusahaan mitra industri terpercaya.
                </p>
              </div>

              <div>
                <Button
                  onClick={handlePostClick}
                  className="rounded-xl bg-[#D71920] hover:bg-[#B8141B] text-white font-bold shadow-md shadow-red-500/20 h-11"
                >
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Pasang Lowongan Alumni
                </Button>
              </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2 relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
                <Input
                  type="text"
                  placeholder="Cari posisi kerja, perusahaan, kota, atau industri..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl h-11 text-sm bg-white text-[#1F2937] border-[#E2E8F0] focus-visible:ring-[#004A99]"
                />
              </div>

              <div>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full h-11 px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-[#1F2937] text-sm focus:outline-none focus:ring-2 focus:ring-[#004A99]"
                >
                  <option value="all">Semua Tipe Pekerjaan</option>
                  {Object.entries(JOB_TYPES).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Job Listings */}
        <section className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-8">
          {!isAuthenticated && (
            <div className="mb-6 rounded-2xl border border-blue-200/90 bg-blue-50/80 p-4 dark:border-blue-900/50 dark:bg-blue-950/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs shadow-xs">
              <div className="flex items-center gap-2.5 text-[#004A99] dark:text-blue-300">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-[#004A99] dark:bg-blue-900/50">
                  <Lock className="h-4 w-4" />
                </div>
                <div>
                  <span className="font-bold">Mode Pratinjau Terbatas (Belum Login):</span>
                  <p className="text-slate-600 dark:text-slate-300 text-[11px] sm:text-xs">
                    Hanya menampilkan ringkasan posisi. Silakan masuk ke akun Alumni Anda untuk membuka rincian lengkap dan fitur komentar.
                  </p>
                </div>
              </div>
              <Link href="/login" className="shrink-0">
                <Button size="sm" className="rounded-xl bg-[#082B52] hover:bg-[#06203D] text-white text-xs font-bold h-9 px-4">
                  <LogIn className="h-3.5 w-3.5 mr-1.5" />
                  Masuk Akun
                </Button>
              </Link>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 text-[#004A99] animate-spin" />
                <p className="text-sm text-[#64748B]">Memuat lowongan...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.length === 0 ? (
                <div className="text-center py-20 bg-card rounded-2xl border border-dashed border-[#E2E8F0] p-8">
                  <Briefcase className="h-12 w-12 text-[#64748B] mx-auto mb-3" />
                  <h3 className="text-base font-bold text-[#1F2937]">Tidak Ada Lowongan Ditemukan</h3>
                  <p className="text-xs text-[#64748B] mt-1">
                    {searchQuery ? 'Coba gunakan kata kunci lain.' : 'Belum ada lowongan yang dipublikasikan.'}
                  </p>
                  {isAlumni && (
                    <Button
                      onClick={() => setShowCreateDialog(true)}
                      className="mt-4 rounded-xl bg-[#D71920] hover:bg-[#B8141B] text-white text-xs font-bold"
                    >
                      <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
                      Jadilah yang Pertama Posting
                    </Button>
                  )}
                </div>
              ) : (
                jobs.map((job) => (
                  <Card
                    key={job.uuid}
                    className="border border-[#E2E8F0] bg-card hover:border-[#004A99]/50 transition-all rounded-2xl shadow-sm overflow-hidden"
                  >
                    <CardContent className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                      <div className="space-y-2.5 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          {job.industry && (
                            <span className="text-xs font-bold text-[#004A99] bg-blue-50 px-2.5 py-0.5 rounded-md border border-blue-100">
                              {job.industry.name}
                            </span>
                          )}
                          <Badge variant="outline" className="text-xs font-semibold border-[#E2E8F0] text-[#64748B]">
                            {JOB_TYPES[job.type] || job.type}
                          </Badge>
                          {job.is_remote && (
                            <Badge className="bg-green-50 text-green-700 border-green-200 text-xs font-semibold">
                              <Globe className="h-3 w-3 mr-1" />Remote
                            </Badge>
                          )}
                          {isAuthenticated && job.posted_by && (
                            <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Diposting oleh: {job.posted_by.name}
                            </span>
                          )}
                        </div>

                        <h3
                          className="text-lg font-bold text-[#1F2937] hover:text-[#004A99] transition-colors cursor-pointer"
                          onClick={() => openJobDetail(job)}
                        >
                          {job.title}
                        </h3>

                        <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-xs text-[#64748B]">
                          {job.company && (
                            <div className="flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-[#64748B]" />
                              <span className="font-semibold text-[#1F2937]">{job.company.name}</span>
                            </div>
                          )}
                          {job.location && (
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 text-[#64748B]" />
                              <span>{job.location}</span>
                            </div>
                          )}
                          {isAuthenticated && job.salary_visible ? (
                            <div className="flex items-center gap-1.5">
                              <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                              <span className="font-medium text-emerald-600">
                                {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                              </span>
                            </div>
                          ) : !isAuthenticated ? (
                            <div className="flex items-center gap-1.5">
                              <Lock className="h-3.5 w-3.5 text-slate-400" />
                              <span className="font-medium text-slate-500 italic">Login untuk lihat gaji</span>
                            </div>
                          ) : null}
                          {job.expires_at && (
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-[#64748B]" />
                              <span>Batas: {new Date(job.expires_at).toLocaleDateString('id-ID')}</span>
                            </div>
                          )}
                          {job.media && job.media.length > 0 && (
                            <div className="flex items-center gap-1.5 text-blue-500">
                              <FileText className="h-3.5 w-3.5" />
                              <span className="font-medium">{job.media.length} lampiran</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openJobDetail(job)}
                          className="rounded-xl text-xs font-semibold border-[#E2E8F0] text-[#1F2937] hover:bg-slate-50"
                        >
                          <Eye className="h-3.5 w-3.5 mr-1.5" />
                          Detail
                        </Button>
                        {isAuthenticated && job.application_url ? (
                          <a href={job.application_url} target="_blank" rel="noopener noreferrer">
                            <Button
                              size="sm"
                              className="rounded-xl bg-[#D71920] hover:bg-[#B8141B] text-white text-xs font-bold shadow-sm"
                            >
                              <Send className="h-3.5 w-3.5 mr-1.5" />
                              Lamar
                            </Button>
                          </a>
                        ) : isAuthenticated && job.contact_info ? (
                          <Button
                            size="sm"
                            onClick={() => {
                              window.open(`mailto:${job.contact_info}?subject=Lamaran: ${job.title}`, '_blank');
                            }}
                            className="rounded-xl bg-[#D71920] hover:bg-[#B8141B] text-white text-xs font-bold shadow-sm"
                          >
                            <Send className="h-3.5 w-3.5 mr-1.5" />
                            Lamar
                          </Button>
                        ) : !isAuthenticated ? (
                          <Link href="/login">
                            <Button
                              size="sm"
                              className="rounded-xl bg-[#082B52] hover:bg-[#06203D] text-white text-xs font-bold shadow-sm"
                            >
                              <Lock className="h-3.5 w-3.5 mr-1.5" />
                              Masuk untuk Melamar
                            </Button>
                          </Link>
                        ) : null}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </section>
      </main>

      {/* Detail Job Modal */}
      {selectedJob && (
        <Dialog open={!!selectedJob} onOpenChange={(open) => !open && setSelectedJob(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6 border-[#E2E8F0]">
            <DialogHeader className="space-y-1 text-left">
              <div className="flex items-center gap-2 flex-wrap">
                {selectedJob.industry && (
                  <Badge className="bg-blue-50 text-[#004A99] border-blue-200 text-xs font-bold">
                    {selectedJob.industry.name}
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs border-[#E2E8F0]">
                  {JOB_TYPES[selectedJob.type] || selectedJob.type}
                </Badge>
                {selectedJob.is_remote && (
                  <Badge className="bg-green-50 text-green-700 border-green-200 text-xs font-semibold">
                    <Globe className="h-3 w-3 mr-1" />Remote
                  </Badge>
                )}
              </div>
              <DialogTitle className="text-xl font-heading font-black text-[#1F2937] pt-1">
                {selectedJob.title}
              </DialogTitle>
              <DialogDescription className="text-xs text-[#64748B]">
                {selectedJob.company?.name && `${selectedJob.company.name} • `}
                {selectedJob.location || 'Lokasi fleksibel'}
                {selectedJob.published_at && ` • ${timeAgo(selectedJob.published_at)}`}
                {selectedJob.view_count > 0 && ` • ${selectedJob.view_count}x dilihat`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4 text-xs">
              {/* Description */}
              <div className="p-3 bg-slate-50 border border-[#E2E8F0] rounded-xl space-y-1">
                <span className="font-semibold text-[#1F2937]">Deskripsi Pekerjaan:</span>
                <p className="text-[#64748B] leading-relaxed whitespace-pre-wrap">{selectedJob.description}</p>
              </div>

              {!isAuthenticated ? (
                <div className="rounded-2xl border border-amber-200/90 bg-amber-50/80 p-5 text-center space-y-3 dark:border-amber-900/50 dark:bg-amber-950/20 shadow-xs">
                  <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400">
                    <Lock className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-heading font-bold text-slate-900 dark:text-white text-sm">
                      Rincian Kualifikasi & Pengajuan Lamaran Terkunci
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-300 max-w-md mx-auto leading-relaxed">
                      Untuk menjaga kredibilitas rujukan almamater, detail kualifikasi, gaji, kontak alumni, serta form lamaran hanya dapat diakses oleh anggota resmi terdaftar.
                    </p>
                  </div>
                  <div className="pt-2 flex justify-center gap-2">
                    <Link href="/login">
                      <Button size="sm" className="rounded-xl bg-[#D71920] hover:bg-[#B8141B] text-white font-bold text-xs shadow-sm">
                        <LogIn className="h-3.5 w-3.5 mr-1.5" />
                        Masuk untuk Melamar
                      </Button>
                    </Link>
                    <Link href="/register">
                      <Button size="sm" variant="outline" className="rounded-xl border-slate-300 text-xs font-semibold">
                        Daftar Akun
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  {/* Qualifications */}
                  {selectedJob.qualifications && (
                    <div className="space-y-2 p-3 bg-slate-50 border border-[#E2E8F0] rounded-xl">
                      <span className="font-semibold text-[#1F2937]">Kualifikasi & Persyaratan:</span>
                      <p className="text-[#64748B] leading-relaxed whitespace-pre-wrap">{selectedJob.qualifications}</p>
                    </div>
                  )}

                  {/* Salary & Meta */}
                  <div className="grid grid-cols-2 gap-3 p-3 border border-[#E2E8F0] rounded-xl bg-card">
                    {selectedJob.salary_visible && (
                      <div>
                        <span className="text-[#64748B]">Perkiraan Gaji:</span>
                        <p className="font-bold text-emerald-600">
                          {formatSalary(selectedJob.salary_min, selectedJob.salary_max, selectedJob.salary_currency)}
                        </p>
                      </div>
                    )}
                    <div>
                      <span className="text-[#64748B]">Diposting oleh:</span>
                      <p className="font-bold text-[#1F2937]">
                        {selectedJob.posted_by?.name}
                        {selectedJob.posted_by?.alumni_profile?.cohort_name &&
                          ` (${selectedJob.posted_by.alumni_profile.cohort_name})`}
                      </p>
                    </div>
                    {selectedJob.contact_info && (
                      <div>
                        <span className="text-[#64748B]">Kontak:</span>
                        <p className="font-bold text-[#1F2937]">{selectedJob.contact_info}</p>
                      </div>
                    )}
                    {selectedJob.expires_at && (
                      <div>
                        <span className="text-[#64748B]">Batas Lamaran:</span>
                        <p className="font-bold text-[#1F2937]">
                          {new Date(selectedJob.expires_at).toLocaleDateString('id-ID', {
                            day: 'numeric', month: 'long', year: 'numeric'
                          })}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Media / Attachments */}
                  {selectedJob.media && selectedJob.media.length > 0 && (
                    <div className="space-y-2">
                      <span className="font-semibold text-[#1F2937]">Lampiran:</span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {selectedJob.media.map((m) => (
                          <a
                            key={m.uuid}
                            href={`${getApiBaseUrl()}/storage/${m.file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-[#E2E8F0] hover:border-[#004A99]/30 transition-colors group"
                          >
                            {m.type === 'image' ? (
                              <ImageIcon className="h-4 w-4 text-blue-500 shrink-0" />
                            ) : (
                              <FileText className="h-4 w-4 text-red-500 shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium text-[#1F2937] group-hover:text-[#004A99] transition-colors">
                                {m.original_name}
                              </p>
                              <p className="text-[10px] text-[#94A3B8]">{formatFileSize(m.file_size)}</p>
                            </div>
                          </a>
                        ))}
                      </div>

                      {/* Image Previews */}
                      {selectedJob.media.filter((m) => m.type === 'image').length > 0 && (
                        <div className="space-y-1.5 mt-2">
                          <p className="text-[11px] text-[#64748B]">Preview Gambar (klik untuk membuka tab baru):</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {selectedJob.media
                              .filter((m) => m.type === 'image')
                              .map((m) => (
                                <a
                                  key={m.uuid}
                                  href={`${getApiBaseUrl()}/storage/${m.file_path}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block overflow-hidden rounded-xl border border-[#E2E8F0] bg-slate-50 hover:border-[#004A99]/50 transition-all group shadow-xs"
                                >
                                  <img
                                    src={`${getApiBaseUrl()}/storage/${m.file_path}`}
                                    alt={m.original_name}
                                    className="w-full max-h-64 object-contain mx-auto p-1 group-hover:scale-[1.02] transition-transform duration-200"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                </a>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Apply CTA */}
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    {selectedJob.application_url && (
                      <a href={selectedJob.application_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button className="w-full rounded-xl bg-[#D71920] hover:bg-[#B8141B] text-white font-bold text-xs">
                          <Send className="h-3.5 w-3.5 mr-1.5" />
                          Lamar via Link
                        </Button>
                      </a>
                    )}
                    {selectedJob.contact_info && (
                      <Button
                        variant="outline"
                        onClick={() => window.open(`mailto:${selectedJob.contact_info}?subject=Lamaran: ${selectedJob.title}`, '_blank')}
                        className="flex-1 rounded-xl border-[#E2E8F0] text-[#1F2937] font-bold text-xs"
                      >
                        <Send className="h-3.5 w-3.5 mr-1.5" />
                        Kirim Email
                      </Button>
                    )}
                  </div>
                </>
              )}

              {/* Comments Section */}
              <div className="border-t border-[#E2E8F0] pt-4">
                <CommentSection jobUuid={selectedJob.uuid} />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="outline" className="rounded-xl border-[#E2E8F0] text-[#1F2937]" onClick={() => setSelectedJob(null)}>
                Tutup
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Job Dialog */}
      <CreateJobDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={fetchJobs}
      />

      <Footer />
    </div>
  );
}
